import type {
  NavigatorProvider,
  NavigatorProviderContext,
} from '@/shared/components/navigator-results';
import type { ProviderProfile } from '@/features/provider-directory/types/provider-profile.types';
import type { useFpQaProviderProfiles } from '../../hooks/useFpProviderProfiles';
import { MatchedProviderCard } from './MatchedProviderCard';
import { AltMatchRow } from './AltMatchRow';

type FpProviderSlotProps = {
  provider: NavigatorProvider;
  context: NavigatorProviderContext;
  query: ReturnType<typeof useFpQaProviderProfiles>[number] | undefined;
  specialtyName: string;
  canRefer: boolean;
  onRefer: (profile: ProviderProfile) => void;
  /** Feature 071 — forwarded to the card. */
  onSendContactCard?: (profile: ProviderProfile) => void;
  /** True when the engagement is explicitly tied to a client (spec §3.3). */
  isClientLinked: boolean;
};

/**
 * FP provider renderer for NavigatorResults: the featured slot gets the rich
 * MatchedProviderCard (selection chip + AI reason), alternates render as
 * AltMatchRow with the Select swap action and the displaced top-match tag.
 */
export const FpProviderSlot = ({
  provider,
  context,
  query,
  specialtyName,
  canRefer,
  onRefer,
  onSendContactCard,
  isClientLinked,
}: FpProviderSlotProps) => {
  if (!query) return null;
  return context.isSelected ? (
    <MatchedProviderCard
      query={query}
      isTopMatch={context.isTopMatch}
      isSelection={!context.isTopMatch}
      isBestMatch={context.isBestMatch}
      aiReason={provider.aiReason}
      specialtyName={specialtyName}
      canRefer={canRefer}
      onRefer={onRefer}
      onSendContactCard={onSendContactCard}
      isClientLinked={isClientLinked}
      location={provider.location}
      specialtyDeliveryModes={provider.specialtyDeliveryModes}
    />
  ) : (
    <AltMatchRow
      query={query}
      specialtyName={specialtyName}
      aiReason={provider.aiReason}
      isTopMatchTag={provider.rankOrder === 1 && !provider.isSelected}
      isBestMatch={context.isBestMatch}
      onSelect={context.onSelect}
      selectDisabled={context.selectDisabled}
      location={provider.location}
      specialtyDeliveryModes={provider.specialtyDeliveryModes}
    />
  );
};
